import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import {
  FileText, Download, Plus, Edit, Archive, Search, ChevronDown,
  ArrowRight, Moon, Car, History, CreditCard, Send, Trash2, Eye, Ship
} from "lucide-react";
import { STATUS_OPTIONS, STATUS_COLORS, STATUS_LABELS, TIME_SLOTS } from "./shared";
import StatusHistoryModal from "../StatusHistoryModal";
import BookingDetailsModal from "./BookingDetailsModal";

export default function BookingsTab({
  quotes, filterStatus, setFilterStatus, searchQuery, setSearchQuery,
  filterOrigin, setFilterOrigin, filterDestination, setFilterDestination,
  filterDateFrom, setFilterDateFrom, filterDateTo, setFilterDateTo,
  filterTimeFrom, setFilterTimeFrom, filterTimeTo, setFilterTimeTo,
  showArchived, setShowArchived,
  uniqueOrigins, uniqueDestinations,
  updateQuoteStatus, archiveQuote, exportBookings, clearFilters, hasActiveFilters,
  onAddBooking, onModifyQuote, onAssignDriver, onTogglePaid, onToggleRefunded,
  onResendPaymentLink, onDeleteBooking
}) {
  const [showFilters, setShowFilters] = useState(false);
  const [historyId, setHistoryId] = useState(null);
  const [detailQuote, setDetailQuote] = useState(null);

  // A confirmed booking missing a driver becomes "urgent" from 24h before
  // pickup and stops blinking as soon as the pickup date+time has passed.
  const needsDriverWarning = (q) => {
    if (q.status !== "confirmed" || q.driver_id) return false;
    if (!q.pickup_date || !q.pickup_time) return false;
    const pickup = new Date(`${q.pickup_date}T${q.pickup_time}:00`);
    if (Number.isNaN(pickup.getTime())) return false;
    const now = Date.now();
    if (now >= pickup.getTime()) return false;    // pickup already gone → stop blinking
    const dayBefore = new Date(pickup.getTime() - 24 * 60 * 60 * 1000);
    return now >= dayBefore.getTime();
  };

  return (
    <div data-testid="bookings-tab">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <h2 className="text-xl font-semibold text-white">Bookings ({quotes.length})</h2>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={exportBookings} className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button onClick={onAddBooking} className="btn-gold" size="sm" data-testid="add-booking-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Booking
          </Button>
        </div>
      </div>

      <div className="bg-[#0E0E0E] border border-white/5 rounded-sm p-4 mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input placeholder="Search name, email, phone, ID..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="input-dark pl-10" data-testid="booking-search" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 input-dark" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className="bg-[#1F1F1F] border-[#333]">
              <SelectItem value="all" className="text-white hover:bg-[#333]">All Active</SelectItem>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s} value={s} className="text-white hover:bg-[#333] capitalize">{STATUS_LABELS[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)}
            className={`text-white/50 hover:text-white ${showFilters ? "bg-white/5" : ""}`}>
            <ChevronDown className={`w-4 h-4 mr-1 transition-transform ${showFilters ? "rotate-180" : ""}`} /> Filters
          </Button>
          <div className="flex items-center gap-2">
            <Switch checked={showArchived} onCheckedChange={setShowArchived} />
            <span className="text-white/40 text-xs">Archived</span>
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-400 hover:text-red-300">Clear All</Button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 pt-3 border-t border-white/5">
            <div>
              <label className="text-white/40 text-[10px] uppercase mb-1 block">Origin</label>
              <Select value={filterOrigin} onValueChange={setFilterOrigin}>
                <SelectTrigger className="input-dark h-9 text-xs"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333]">
                  <SelectItem value="all" className="text-white hover:bg-[#333] text-xs">All Origins</SelectItem>
                  {uniqueOrigins.map(o => <SelectItem key={o} value={o} className="text-white hover:bg-[#333] text-xs">{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase mb-1 block">Destination</label>
              <Select value={filterDestination} onValueChange={setFilterDestination}>
                <SelectTrigger className="input-dark h-9 text-xs"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">
                  <SelectItem value="all" className="text-white hover:bg-[#333] text-xs">All Destinations</SelectItem>
                  {uniqueDestinations.map(d => <SelectItem key={d} value={d} className="text-white hover:bg-[#333] text-xs">{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase mb-1 block">Date From</label>
              <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="input-dark h-9 text-xs" />
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase mb-1 block">Date To</label>
              <Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="input-dark h-9 text-xs" />
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase mb-1 block">Time From</label>
              <Select value={filterTimeFrom} onValueChange={setFilterTimeFrom}>
                <SelectTrigger className="input-dark h-9 text-xs"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">
                  <SelectItem value="all" className="text-white hover:bg-[#333] text-xs">Any Time</SelectItem>
                  {TIME_SLOTS.map(t => <SelectItem key={t} value={t} className="text-white hover:bg-[#333] text-xs">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase mb-1 block">Time To</label>
              <Select value={filterTimeTo} onValueChange={setFilterTimeTo}>
                <SelectTrigger className="input-dark h-9 text-xs"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">
                  <SelectItem value="all" className="text-white hover:bg-[#333] text-xs">Any Time</SelectItem>
                  {TIME_SLOTS.map(t => <SelectItem key={t} value={t} className="text-white hover:bg-[#333] text-xs">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {quotes.length === 0 ? (
        <div className="text-center py-12"><FileText className="w-12 h-12 text-white/20 mx-auto mb-4" /><p className="text-white/50">No bookings found</p></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="quotes-table">
            <thead>
              <tr><th>Date</th><th>ID</th><th>Customer</th><th>Route</th><th>Pickup</th><th>Type</th><th>Price</th><th>Paid</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {quotes.map((q) => {
                const urgent = needsDriverWarning(q);
                return (
                <tr key={q.quote_id} data-testid={`quote-row-${q.quote_id}`}
                  className={`${q.status === "archived" ? "opacity-50" : ""} ${urgent ? "row-needs-driver" : ""} ${q.status === "cancelled" ? "row-cancelled" : ""} ${q.refunded ? "row-refunded" : ""} ${q.paid ? "row-paid" : ""}`}>
                  <td className="text-white/50 font-mono text-xs">{q.created_at?.split("T")[0]}</td>
                  <td className="text-[#C5A059] font-mono text-[11px] whitespace-nowrap" title={q.quote_id} data-testid={`quote-id-${q.quote_id}`}>{q.quote_id}</td>
                  <td>
                    <button
                      onClick={() => setDetailQuote(q)}
                      className="text-left hover:text-[#C5A059] transition-colors"
                      title="View full booking details"
                      data-testid={`open-details-name-${q.quote_id}`}
                    >
                      <div className="text-white text-sm">{q.user_name}</div>
                      <div className="text-white/40 text-xs">{q.user_email}</div>
                      <div className="text-white/40 text-xs">{q.user_phone}</div>
                    </button>
                  </td>
                  <td>
                    {q.venice_hotel_name && (
                      <div className="flex items-center gap-1.5 mb-0.5" data-testid={`hotel-badge-${q.quote_id}`}>
                        <Ship className="w-3.5 h-3.5 text-[#C5A059] flex-shrink-0" title="Venice hotel transfer — water taxi included" />
                        <span className="text-[#C5A059] text-xs font-medium truncate max-w-[220px]" title={q.venice_hotel_name}>
                          {q.venice_hotel_name}
                        </span>
                      </div>
                    )}
                    <div className="text-white text-sm flex items-center gap-1">
                      <span className="truncate max-w-[100px]" title={q.origin_specific_address ? `${q.origin}: ${q.origin_specific_address}` : q.origin}>{q.origin}</span>
                      {q.origin === "Venice - Water Taxi" && (
                        <Ship className="w-3 h-3 text-sky-400 flex-shrink-0" title="Water taxi pickup" data-testid={`water-taxi-origin-${q.quote_id}`} />
                      )}
                      {q.origin === "Venice - Hotel" && (
                        <Ship className="w-3 h-3 text-[#C5A059] flex-shrink-0" title="Hotel pickup — water taxi included" data-testid={`hotel-origin-${q.quote_id}`} />
                      )}
                      <ArrowRight className="w-3 h-3 text-[#C5A059] flex-shrink-0" />
                      <span className="truncate max-w-[100px]" title={q.destination_specific_address ? `${q.destination}: ${q.destination_specific_address}` : q.destination}>{q.destination}</span>
                      {q.destination === "Venice - Water Taxi" && (
                        <Ship className="w-3 h-3 text-sky-400 flex-shrink-0" title="Water taxi drop-off" data-testid={`water-taxi-dest-${q.quote_id}`} />
                      )}
                      {q.destination === "Venice - Hotel" && (
                        <Ship className="w-3 h-3 text-[#C5A059] flex-shrink-0" title="Hotel drop-off — water taxi included" data-testid={`hotel-dest-${q.quote_id}`} />
                      )}
                    </div>
                    {(q.origin_specific_address || q.destination_specific_address) && (
                      <div className="mt-1 space-y-0.5">
                        {q.origin_specific_address && (
                          <div className="text-[10px] text-amber-300/90" data-testid={`origin-addr-${q.quote_id}`}>
                            <span className="text-amber-400/70 uppercase mr-1">
                              {q.origin === "Venice - Near Venice" ? "Near Venice" : q.origin === "Venice - Water Taxi" ? "Water Taxi" : "Custom Address"}:
                            </span>
                            <span className="text-white/70">{q.origin_specific_address}</span>
                          </div>
                        )}
                        {q.destination_specific_address && (
                          <div className="text-[10px] text-amber-300/90" data-testid={`dest-addr-${q.quote_id}`}>
                            <span className="text-amber-400/70 uppercase mr-1">
                              {q.destination === "Venice - Near Venice" ? "Near Venice" : q.destination === "Venice - Water Taxi" ? "Water Taxi" : "Custom Address"}:
                            </span>
                            <span className="text-white/70">{q.destination_specific_address}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="text-white text-sm">{q.pickup_date}</div>
                    <div className="text-white/40 text-xs flex items-center gap-1">
                      {q.pickup_time} {q.is_night_service && <Moon className="w-3 h-3 text-[#C5A059]" />}
                    </div>
                    {q.is_round_trip && q.return_date && (
                      <div className="text-white/30 text-xs mt-0.5">Return: {q.return_date} {q.return_time}</div>
                    )}
                  </td>
                  <td>
                    <div className="flex flex-wrap items-center gap-1" data-testid={`type-cell-${q.quote_id}`}>
                      {q.is_round_trip && <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">RT</span>}
                      {q.luxury_options_snapshot && q.luxury_options_snapshot.length > 0 ? (
                        q.luxury_options_snapshot.map((lb) => (
                          <span
                            key={lb.luxury_option_id}
                            className="text-[10px] bg-[#C5A059]/20 text-[#C5A059] px-1.5 py-0.5 rounded"
                            title={`+${lb.surcharge_pct}% surcharge`}
                          >
                            {lb.title}
                          </span>
                        ))
                      ) : q.is_luxury ? (
                        <span className="text-[10px] bg-[#C5A059]/20 text-[#C5A059] px-1.5 py-0.5 rounded">LUX</span>
                      ) : null}
                      {q.vehicle_title && (
                        <span className="text-[10px] bg-purple-500/15 text-purple-300 px-1.5 py-0.5 rounded" title={q.vehicle_autoassigned ? "Auto-assigned (internal)" : "Manually assigned"}>
                          {q.vehicle_title}{q.vehicle_autoassigned ? " ⓘ" : ""}
                        </span>
                      )}
                      {!q.is_round_trip && !q.is_luxury && (!q.luxury_options_snapshot || q.luxury_options_snapshot.length === 0) && !q.vehicle_title && (
                        <span className="text-white/30 text-xs">Std</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="text-[#C5A059] font-semibold font-mono">{"\u20AC"}{q.total_price?.toFixed(2)}</div>
                    {q.night_surcharge > 0 && <div className="text-white/40 text-[10px]">+{"\u20AC"}{q.night_surcharge} night</div>}
                    {q.round_trip_discount > 0 && <div className="text-emerald-400 text-[10px]">-{"\u20AC"}{q.round_trip_discount} RT</div>}
                  </td>
                  <td>
                    <label className="inline-flex items-center cursor-pointer" title={q.paid ? "Marked PAID" : "Mark as paid"} data-testid={`paid-cell-${q.quote_id}`}>
                      <input
                        type="checkbox"
                        checked={!!q.paid}
                        onChange={(e) => onTogglePaid(q.quote_id, e.target.checked)}
                        className="w-4 h-4 accent-emerald-500 cursor-pointer"
                        data-testid={`paid-toggle-${q.quote_id}`}
                      />
                      {q.paid && <CreditCard className="w-3 h-3 ml-1 text-emerald-400" />}
                    </label>
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded text-[10px] uppercase border whitespace-nowrap ${STATUS_COLORS[q.status]}`}>
                      {STATUS_LABELS[q.status] || q.status}
                    </span>
                    {q.refunded && (
                      <div className="text-pink-400 text-[10px] mt-1 flex items-center gap-1 uppercase tracking-wide" data-testid={`refunded-badge-${q.quote_id}`}>
                        <CreditCard className="w-3 h-3" /> Refunded
                      </div>
                    )}
                    {q.driver_name && (
                      <div className="text-violet-400 text-[10px] mt-1 flex items-center gap-1" title={`Driver: ${q.driver_name}`}>
                        <Car className="w-3 h-3" /> {q.driver_name}
                      </div>
                    )}
                    {urgent && !q.driver_id && (
                      <div className="text-amber-400 text-[10px] mt-1 flex items-center gap-1 font-semibold uppercase tracking-wide icon-pulse">
                        <Car className="w-3 h-3" /> Driver missing
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Select value={q.status} onValueChange={(v) => updateQuoteStatus(q.quote_id, v)}>
                        <SelectTrigger className="w-28 h-8 bg-[#1F1F1F] border-[#333] text-white text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1F1F1F] border-[#333]">
                          {STATUS_OPTIONS.filter(s => s !== "archived").map(s => (
                            <SelectItem key={s} value={s} className="text-white hover:bg-[#333] capitalize text-xs">{STATUS_LABELS[s]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {q.status === "cancelled" && (
                        <label className="inline-flex items-center gap-1 ml-1 cursor-pointer text-[10px] text-pink-300 uppercase whitespace-nowrap" title="Issue refund (sends notification email)" data-testid={`refunded-cell-${q.quote_id}`}>
                          <input
                            type="checkbox"
                            checked={!!q.refunded}
                            onChange={(e) => onToggleRefunded?.(q.quote_id, e.target.checked)}
                            className="w-3.5 h-3.5 accent-pink-500 cursor-pointer"
                            data-testid={`refunded-toggle-${q.quote_id}`}
                          />
                          Refund
                        </label>
                      )}
                      {q.status === "confirmed" && (
                        <button onClick={() => onAssignDriver(q)}
                          className={`p-1.5 transition-colors ${urgent ? "text-amber-400 hover:text-amber-300 icon-pulse" : q.driver_id ? "text-violet-400 hover:text-violet-300" : "text-amber-400 hover:text-amber-300"}`}
                          title={q.driver_id ? `Change driver (currently: ${q.driver_name})` : "Assign driver"}
                          data-testid={`assign-driver-${q.quote_id}`}>
                          <Car className="w-4 h-4" />
                        </button>
                      )}
                      {q.status === "awaiting_payment" && !q.paid && (
                        <button onClick={() => onResendPaymentLink && onResendPaymentLink(q)}
                          className="p-1.5 text-orange-400 hover:text-orange-300 transition-colors"
                          title="Resend payment link to customer"
                          data-testid={`resend-payment-${q.quote_id}`}>
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => setDetailQuote(q)} className="p-1.5 text-white/40 hover:text-[#C5A059] transition-colors" title="View full booking details"
                        data-testid={`view-details-${q.quote_id}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                      {onDeleteBooking && (
                        <button onClick={() => onDeleteBooking(q)}
                          className="p-1.5 text-white/40 hover:text-red-400 transition-colors"
                          title="Delete booking (irreversible)"
                          data-testid={`delete-booking-${q.quote_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => onModifyQuote(q)} className="p-1.5 text-white/40 hover:text-violet-400 transition-colors" title="Modify & send proposal"
                        data-testid={`modify-${q.quote_id}`}>
                        <Edit className="w-4 h-4" />
                      </button>
                      {q.status !== "archived" && (
                        <button onClick={() => archiveQuote(q.quote_id)} className="p-1.5 text-white/40 hover:text-white/70 transition-colors" title="Archive"
                          data-testid={`archive-${q.quote_id}`}>
                          <Archive className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => setHistoryId(q.quote_id)} className="p-1.5 text-white/40 hover:text-[#C5A059] transition-colors" title="View status history"
                        data-testid={`history-${q.quote_id}`}>
                        <History className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {historyId && (
        <StatusHistoryModal quoteId={historyId} onClose={() => setHistoryId(null)} />
      )}
      {detailQuote && (
        <BookingDetailsModal
          quote={detailQuote}
          onClose={() => setDetailQuote(null)}
          onModify={onModifyQuote}
        />
      )}
    </div>
  );
}
